import {
  Dialog,
  DialogBackdrop,
  DialogPanel,
  DialogTitle,
} from "@headlessui/react";
import { XMarkIcon } from "@heroicons/react/24/outline";
import {
  FC,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import { useTranslation } from "react-i18next";
import { toast } from "react-toastify";
import { useCreateMutation, useDestroyMutation } from "../../api/genericHooks";
import { BookContext } from "../../contexts/BookContext";
import { Image } from "../../types/models/Image";
import SmartImageUploader from "../SmartImageUploader";

type AddPicturesModalProps = {
  open: boolean;
  setOpen: (value: boolean) => void;
  chapter_id: number;
};

export const AddPicturesModal: FC<AddPicturesModalProps> = ({
  open,
  setOpen,
  chapter_id,
}) => {
  const { t } = useTranslation();
  const [images, setImages] = useState<Image[]>([]);
  const { generated_chapters, media, refresh } = useContext(BookContext);
  const generatedChapter = useMemo(
    () => generated_chapters.find((gc) => gc.chapter_id === chapter_id),
    [generated_chapters, chapter_id]
  );
  const relatedMedia = useMemo(
    () =>
      media.filter(
        (m) =>
          m.mediable_id === generatedChapter?.id &&
          m.mediable_type === "GeneratedChapter" &&
          m.file_type === "image"
      ),
    [media, generatedChapter]
  );

  useEffect(() => {
    if (generatedChapter && relatedMedia) {
      const images = relatedMedia
        .map((m) => ({
          id: Number(m.id),
          url: m.url,
        }))
        .reverse();
      setImages(images);
    }
  }, [generatedChapter, relatedMedia, setImages]);

  const saveImagesMutation = useCreateMutation("/media", {}, false, {
    headers: { "Content-Type": "multipart/form-data" },
  });
  const deleteImageMutation = useDestroyMutation("/media");

  const onSubmit = () => {
    setOpen(false);
    refresh();
  };

  const handleAddImage = useCallback(
    (file: File) => {
      const formData = new FormData();
      formData.append("medium[mediable_type]", "GeneratedChapter");
      formData.append("medium[chapter_id]", String(chapter_id));
      formData.append("medium[file_type]", "image");
      formData.append("medium[file]", file);

      saveImagesMutation
        .mutateAsync(formData)
        .then(() => {
          refresh();
          toast.success(t("addPicturesModal.uploadSuccess"));
        })
        .catch(() => {
          toast.error(t("addPicturesModal.uploadError"));
        });
    },
    [saveImagesMutation, chapter_id, refresh, t]
  );

  const handleRemoveImage = useCallback(
    (id: number) => {
      deleteImageMutation.mutateAsync(id).then(() => {
        refresh();
        toast.success(t("addPicturesModal.deleteSuccess"));
      });
    },
    [deleteImageMutation, refresh, t]
  );

  return (
    <Dialog open={open} onClose={setOpen} className="relative z-10">
      <DialogBackdrop
        transition
        className="fixed inset-0 bg-gray-500/75 transition-opacity data-[closed]:opacity-0 data-[enter]:duration-300 data-[leave]:duration-200 data-[enter]:ease-out data-[leave]:ease-in"
      />

      <div className="fixed inset-0 z-10 w-screen overflow-y-auto">
        <div className="flex min-h-full items-end justify-center p-4 text-center sm:items-center sm:p-0">
          <DialogPanel
            transition
            className="relative transform overflow-hidden rounded-lg bg-white px-4 pb-4 pt-5 text-left shadow-xl transition-all data-[closed]:translate-y-4 data-[closed]:opacity-0 data-[enter]:duration-300 data-[leave]:duration-200 data-[enter]:ease-out data-[leave]:ease-in sm:my-8 sm:w-full sm:max-w-6xl sm:p-6 data-[closed]:sm:translate-y-0 data-[closed]:sm:scale-95"
          >
            <div className="absolute right-0 top-0 hidden pr-4 pt-4 sm:block">
              <button
                type="button"
                onClick={() => setOpen(false)}
                className="rounded bg-white text-slate-400 hover:text-slate-500 focus:outline-none focus:ring-2 focus:ring-delicate-500 focus:ring-offset-2"
              >
                <span className="sr-only">Close</span>
                <XMarkIcon aria-hidden="true" className="size-6" />
              </button>
            </div>
            <div className="sm:flex sm:items-start">
              <div className="mt-3 text-center sm:ml-4 sm:mt-0 sm:text-left w-full">
                <DialogTitle
                  as="h3"
                  className="text-2xl font-semibold text-slate-800"
                >
                  {t("addPicturesModal.title")}
                </DialogTitle>
                <hr className="mt-2 w-full border-gray-200" />
                <div className="mt-5">
                  <div className="mt-5 mx-auto">
                    <SmartImageUploader
                      images={images}
                      setImages={setImages}
                      onAddImage={handleAddImage}
                      onRemoveImage={handleRemoveImage}
                      label={t("addPicturesModal.upload")}
                      accept="image/*"
                    />
                  </div>
                </div>
              </div>
            </div>
            <div className="mt-5 sm:mt-4 sm:flex sm:flex-row-reverse">
              <button
                type="button"
                onClick={onSubmit}
                className="mt-3 inline-flex w-full justify-center rounded bg-rust-50 px-3 py-2 text-sm font-semibold text-white shadow-sm ring-1 ring-inset ring-rust-300 hover:bg-rust-100 sm:mt-0 sm:w-auto"
              >
                {t("addPicturesModal.save")}
              </button>
            </div>
          </DialogPanel>
        </div>
      </div>
    </Dialog>
  );
};
